import { PropsWithChildren, useEffect, useState } from "react";
import { Box, Button, Container } from "react-bulma-components";
import { useStoredState } from "hooks/useStoredState";
import { Background } from "components/StartGame/Background/Background";
import { useT } from "i18n/I18nProvider";

import styles from "./styles.module.scss";

const MOBILE_WARNING_KEY = "mobileWarningAccepted";
const MOBILE_QUERY = "(max-width: 1024px) and (pointer: coarse)";

const getIsMobileDevice = () => {
  if (typeof window === "undefined") {
    return false;
  }

  const isSmallTouchViewport = window.matchMedia(MOBILE_QUERY).matches;
  const isMobileUserAgent =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    );
  const isTouchDevice = navigator.maxTouchPoints > 0;

  return isSmallTouchViewport || (isTouchDevice && isMobileUserAgent);
};

export const MobileWarningGate = ({ children }: PropsWithChildren<{}>) => {
  const t = useT();
  const [isWarningAccepted, setIsWarningAccepted] = useStoredState<boolean>(
    MOBILE_WARNING_KEY,
    () => false,
  );
  const [isMobileDevice, setIsMobileDevice] = useState(getIsMobileDevice);

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_QUERY);
    const updateIsMobileDevice = () => {
      setIsMobileDevice(getIsMobileDevice());
    };

    updateIsMobileDevice();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", updateIsMobileDevice);

      return () => {
        mediaQuery.removeEventListener("change", updateIsMobileDevice);
      };
    }

    mediaQuery.addListener(updateIsMobileDevice);

    return () => {
      mediaQuery.removeListener(updateIsMobileDevice);
    };
  }, []);

  if (!isMobileDevice || isWarningAccepted) {
    return <>{children}</>;
  }

  return (
    <div className={styles.page}>
      <Background />
      <div className={styles.overlay} />
      <Container className={styles.content}>
        <Box className={styles.box}>
          <h1 className={styles.title}>{t("mobile.title")}</h1>
          <p className={styles.description}>{t("mobile.description.1")}</p>
          <p className={styles.description}>{t("mobile.description.2")}</p>
          <Button
            color="primary"
            fullwidth
            onClick={() => setIsWarningAccepted(true)}
          >
            {t("common.ok")}
          </Button>
        </Box>
      </Container>
    </div>
  );
};
